import { useEffect, useState, type FormEvent } from 'react';
import {
  MARKETPLACE_CATEGORIES,
  MAX_PRODUCT_IMAGES,
  MAX_PRODUCTS_PER_SHOP,
  type MarketplaceCategory,
  type MarketplaceProduct,
  type MarketplaceShop,
} from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { uploadMarketplaceImage } from '@/lib/bunnyStorage';
import { normalizeProduct } from '@/features/marketplace/normalize';
import { useToast } from '@/features/ui/toast/useToast';

interface Props {
  projectId: string;
}

type ProductForm = {
  name: string;
  description: string;
  category: MarketplaceCategory;
  price: number;
  currency: string;
  video_url: string;
  is_published: boolean;
};

const emptyProduct: ProductForm = {
  name: '',
  description: '',
  category: MARKETPLACE_CATEGORIES[0],
  price: 0,
  currency: 'XOF',
  video_url: '',
  is_published: true,
};

export function PlanificationShopPanel({ projectId }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [shop, setShop] = useState<MarketplaceShop | null>(null);
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [shopForm, setShopForm] = useState({ name: '', description: '' });
  const [productForm, setProductForm] = useState(emptyProduct);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!profile) return;
    const { data: shopRow, error: shopErr } = await kuunda
      .from('marketplace_shops')
      .select('*')
      .eq('user_id', profile.id)
      .maybeSingle();
    if (shopErr) {
      // Migration pas encore appliquée
      if (shopErr.message.includes('marketplace_shops') || shopErr.code === '42P01') {
        toast.error('Migration boutique requise (marketplace_shops). Appliquez 20260719170000.');
        return;
      }
      toast.error(shopErr.message);
      return;
    }
    setShop((shopRow as MarketplaceShop) ?? null);
    if (shopRow) {
      setShopForm({ name: shopRow.name, description: shopRow.description ?? '' });
      const { data: prods, error: pErr } = await kuunda
        .from('marketplace_products')
        .select('*')
        .eq('shop_id', shopRow.id)
        .order('created_at', { ascending: false });
      if (pErr) toast.error(pErr.message);
      else setProducts((prods ?? []).map((r) => normalizeProduct(r as Record<string, unknown>)));
    } else {
      setProducts([]);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, projectId]);

  async function createShop(e: FormEvent) {
    e.preventDefault();
    if (!profile) return;
    setLoading(true);
    try {
      const { data, error: err } = await kuunda
        .from('marketplace_shops')
        .insert({
          user_id: profile.id,
          project_id: projectId,
          name: shopForm.name.trim(),
          description: shopForm.description.trim(),
        })
        .select('*')
        .single();
      if (err) throw err;
      setShop(data as MarketplaceShop);
      toast.success(
        'Boutique créée. Ajoutez jusqu’à 10 produits. Gestion aussi disponible dans Pilotage.',
        'MarketPlace',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Création boutique impossible');
    } finally {
      setLoading(false);
    }
  }

  async function onPickImages(files: FileList | null) {
    if (!profile || !files?.length) return;
    setLoading(true);
    try {
      const next = [...photoUrls];
      for (const file of Array.from(files)) {
        if (next.length >= MAX_PRODUCT_IMAGES) break;
        if (!file.type.startsWith('image/')) continue;
        const uploaded = await uploadMarketplaceImage({ userId: profile.id, file });
        next.push(uploaded.url);
      }
      setPhotoUrls(next.slice(0, MAX_PRODUCT_IMAGES));
      toast.info(`Images : ${Math.min(next.length, MAX_PRODUCT_IMAGES)}/${MAX_PRODUCT_IMAGES}`, 'MarketPlace');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload image impossible');
    } finally {
      setLoading(false);
    }
  }

  function startEdit(p: MarketplaceProduct) {
    setEditingId(p.id);
    setProductForm({
      name: p.name,
      description: p.description,
      category: (p.category as MarketplaceCategory) || MARKETPLACE_CATEGORIES[0],
      price: p.price,
      currency: p.currency || 'XOF',
      video_url: p.video_url || '',
      is_published: p.is_published,
    });
    setPhotoUrls(p.photo_urls ?? []);
  }

  function resetProductForm() {
    setEditingId(null);
    setProductForm(emptyProduct);
    setPhotoUrls([]);
  }

  async function saveProduct(e: FormEvent) {
    e.preventDefault();
    if (!profile || !shop) return;
    if (!editingId && products.length >= MAX_PRODUCTS_PER_SHOP) {
      toast.error(`Limite atteinte : ${MAX_PRODUCTS_PER_SHOP} produits par boutique.`);
      return;
    }
    if (photoUrls.length > MAX_PRODUCT_IMAGES) {
      toast.error(`Maximum ${MAX_PRODUCT_IMAGES} images par produit.`);
      return;
    }
    setLoading(true);
    try {
      const seller =
        [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.email;
      const payload = {
        owner_id: profile.id,
        shop_id: shop.id,
        project_id: projectId,
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        category: productForm.category,
        price: Number(productForm.price) || 0,
        currency: productForm.currency,
        photo_urls: photoUrls.slice(0, MAX_PRODUCT_IMAGES),
        video_url: productForm.video_url.trim() || null,
        seller_name: seller,
        seller_email: profile.email,
        seller_phone: profile.phone_number,
        is_published: productForm.is_published,
      };
      if (editingId) {
        const { error: err } = await kuunda.from('marketplace_products').update(payload).eq('id', editingId);
        if (err) throw err;
        toast.success('Produit mis à jour.', 'MarketPlace');
      } else {
        const { error: err } = await kuunda.from('marketplace_products').insert(payload);
        if (err) throw err;
        toast.success('Produit ajouté.', 'MarketPlace');
      }
      resetProductForm();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement produit impossible');
    } finally {
      setLoading(false);
    }
  }

  async function deleteProduct(id: string) {
    if (!window.confirm('Supprimer ce produit ?')) return;
    const { error: err } = await kuunda.from('marketplace_products').delete().eq('id', id);
    if (err) toast.error(err.message);
    else {
      toast.success('Produit supprimé.', 'MarketPlace');
      await load();
    }
  }

  return (
    <section className="panel">
      <h3 style={{ marginTop: 0 }}>ISBD MarketPlace</h3>
      <p className="muted">
        Une seule boutique par abonné · {MAX_PRODUCTS_PER_SHOP} produits max · {MAX_PRODUCT_IMAGES}{' '}
        images hébergées / produit · vidéo = lien externe (optionnel).
      </p>

      {!shop ? (
        <form onSubmit={(e) => void createShop(e)}>
          <div className="field">
            <label>Nom de la boutique</label>
            <input
              required
              value={shopForm.name}
              onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Description</label>
            <textarea
              value={shopForm.description}
              onChange={(e) => setShopForm({ ...shopForm, description: e.target.value })}
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            Créer ma boutique
          </button>
        </form>
      ) : (
        <>
          <p>
            <strong>{shop.name}</strong>
            <span className="muted"> — {products.length}/{MAX_PRODUCTS_PER_SHOP} produits</span>
          </p>
          {shop.description ? <p className="muted">{shop.description}</p> : null}

          <h4>{editingId ? 'Modifier le produit' : 'Ajouter un produit'}</h4>
          <form onSubmit={(e) => void saveProduct(e)}>
            <div className="grid-2">
              <div className="field">
                <label>Nom</label>
                <input
                  required
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Catégorie</label>
                <select
                  value={productForm.category}
                  onChange={(e) =>
                    setProductForm({ ...productForm, category: e.target.value as MarketplaceCategory })
                  }
                >
                  {MARKETPLACE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Prix</label>
                <input
                  type="number"
                  min={0}
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: Number(e.target.value) })}
                />
              </div>
              <div className="field">
                <label>Devise</label>
                <input
                  value={productForm.currency}
                  onChange={(e) => setProductForm({ ...productForm, currency: e.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label>Description</label>
              <textarea
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Images ({photoUrls.length}/{MAX_PRODUCT_IMAGES}) — hébergées par le système</label>
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={loading || photoUrls.length >= MAX_PRODUCT_IMAGES}
                onChange={(e) => void onPickImages(e.target.files)}
              />
              {photoUrls.length ? (
                <ul>
                  {photoUrls.map((u) => (
                    <li key={u}>
                      <a href={u} target="_blank" rel="noreferrer">
                        image
                      </a>{' '}
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setPhotoUrls((prev) => prev.filter((x) => x !== u))}
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="field">
              <label>Lien vidéo (optionnel — vous hébergez la vidéo)</label>
              <input
                type="url"
                placeholder="https://"
                value={productForm.video_url}
                onChange={(e) => setProductForm({ ...productForm, video_url: e.target.value })}
              />
            </div>
            <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={productForm.is_published}
                onChange={(e) => setProductForm({ ...productForm, is_published: e.target.checked })}
              />
              Publié
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {editingId ? 'Mettre à jour' : 'Ajouter le produit'}
              </button>
              {editingId ? (
                <button type="button" className="btn btn-ghost" onClick={resetProductForm}>
                  Annuler
                </button>
              ) : null}
            </div>
          </form>

          <h4>Produits de la boutique</h4>
          {products.length === 0 ? <p className="muted">Aucun produit pour l’instant.</p> : null}
          <ul>
            {products.map((p) => (
              <li key={p.id} style={{ marginBottom: '0.5rem' }}>
                <strong>{p.name}</strong> — {p.price} {p.currency}{' '}
                <button type="button" className="btn btn-ghost" onClick={() => startEdit(p)}>
                  Modifier
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => void deleteProduct(p.id)}>
                  Supprimer
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
